/**
 * Partner Notification Bell - Real-time notifications for internal partners
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePartnerNotifications } from '@/hooks/usePartnerNotifications';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Bell, Check, CheckCheck, MessageSquare, Calendar, UserPlus, FileText, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

const translations = {
  en: {
    notifications: "Notifications",
    markAllRead: "Mark all read",
    noNotifications: "No notifications",
    viewAll: "View All",
    join: "Join",
    new: "New",
  },
  fr: {
    notifications: "Notifications",
    markAllRead: "Tout marquer lu",
    noNotifications: "Aucune notification",
    viewAll: "Voir Tout",
    join: "Rejoindre",
    new: "Nouveau",
  },
};

const notificationIcons: Record<string, any> = {
  message: MessageSquare,
  meeting_reminder: Calendar,
  new_client: UserPlus,
  mandate_update: FileText,
};

interface PartnerNotificationBellProps {
  className?: string;
}

export function PartnerNotificationBell({ className }: PartnerNotificationBellProps) {
  const navigate = useNavigate();
  const { partnerType } = useParams<{ partnerType: string }>();
  const { language } = useLanguage();
  const t = translations[language as 'en' | 'fr'] || translations.en;
  const dateLocale = language === 'fr' ? fr : enUS;
  
  const { 
    notifications, 
    unreadCount, 
    loading, 
    markAsRead, 
    markAllAsRead 
  } = usePartnerNotifications(partnerType);

  const [open, setOpen] = useState(false);

  const handleNotificationClick = (notification: typeof notifications[0]) => {
    markAsRead(notification.id);
    if (notification.actionUrl) {
      if (notification.actionUrl.startsWith('http')) {
        window.open(notification.actionUrl, '_blank');
      } else {
        navigate(notification.actionUrl);
      }
      setOpen(false);
    }
  };

  const formatTime = (date: Date) => {
    return formatDistanceToNow(date, { addSuffix: false, locale: dateLocale });
  };

  const getIcon = (type: string) => {
    const Icon = notificationIcons[type] || Bell;
    return Icon;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={`relative ${className}`}
          aria-label={t.notifications}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs animate-pulse"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-80 p-0" 
        align="end"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
          <h3 className="font-semibold">{t.notifications}</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 text-xs"
              onClick={markAllAsRead}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              {t.markAllRead}
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <ScrollArea className="h-[350px]">
          {loading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{t.noNotifications}</p>
            </div>
          ) : (
            <div>
              {notifications.map((notification) => {
                const Icon = getIcon(notification.type);
                const isMeetingReminder = notification.type === 'meeting_reminder';
                
                return (
                  <div
                    key={notification.id}
                    className={`flex gap-3 px-4 py-3 cursor-pointer hover:bg-accent transition-colors border-b last:border-b-0 ${
                      !notification.seen ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                      isMeetingReminder 
                        ? 'bg-orange-500/10 text-orange-600' 
                        : notification.type === 'message'
                          ? 'bg-blue-500/10 text-blue-600'
                          : 'bg-primary/10 text-primary'
                    }`}>
                      <Icon className="h-5 w-5" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm line-clamp-1 ${!notification.seen ? 'font-semibold' : ''}`}>
                          {notification.title}
                        </p>
                        {!notification.seen && (
                          <Badge variant="default" className="text-[10px] px-1.5 py-0">
                            {t.new}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {notification.message}
                      </p>
                      <div className="flex items-center justify-between mt-1.5">
                        <p className="text-xs text-muted-foreground">
                          {formatTime(notification.createdAt)}
                        </p>
                        {isMeetingReminder && notification.actionUrl && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-6 text-xs px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(notification.actionUrl, '_blank');
                            }}
                          >
                            {t.join}
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {notifications.length > 5 && (
          <div className="border-t p-2">
            <Button 
              variant="ghost" 
              className="w-full text-sm"
              onClick={() => {
                navigate(`/internal-partners/${partnerType}/notifications`);
                setOpen(false);
              }}
            >
              {t.viewAll}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}